// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { priceSecondarySpacingStyles, priceStyles, priceVatLabelStyles } from "@store/Themes/common/mixins/price";
import { ProductLineComponentModel } from "@store/Library/Components/ProductLine/ProductLine";
import ProductPrice, { ProductPriceProps, ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@store/Overrides/Components/SmallHeadingAndText";
import { productLineV2Props } from "@store/Themes/common/props/productLineV2";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import React, { FC } from "react";
import { css } from "styled-components";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import { productLineProps } from "@store/Themes/common/props/productLine";

export interface ProductLinePricePresentationProps extends ExpandStyles<ProductLinePriceStyles> {}

export interface ProductLinePriceProps
    extends Omit<ProductPriceProps, "extendedStyles" | "overridedStyles" | "product">,
        ProductLinePricePresentationProps {
    productLine?: ProductLineComponentModel;
    previousValue?: string;
    /** Overrides the heading label (used when rendering a non-price value such as delivered amount). */
    labelOverride?: string;
    /** Overrides the displayed value (used when rendering a non-price value such as delivered amount). */
    valueOverride?: string;
}

export interface ProductLinePriceStyles {
    productPrice?: ProductPriceStyles;
    smallHeadingAndText?: SmallHeadingAndTextStyles;
    priceUnitOfMeasure?: TypographyPresentationProps;
    vatNote?: TypographyPresentationProps;
    vatPrice?: TypographyPresentationProps;
    changeTooltipAlert?: TooltipPresentationProps;
}

export const productLinePriceStyles: ProductLinePriceStyles = {
    productPrice: {
        priceLabelText: productLineV2Props.infoLabel,
        wrapper: {
            css: css`
                font-weight: ${x => x.theme.design.typography.weight.base};
            `,
        },
        price: {
            saleLabelText: {
                css: css`
                    display: block;
                    font-size: ${x => x.theme.design.typography.size.smaller};
                `,
            },
        },
    },
    priceUnitOfMeasure: productLineProps.savedPrice?.unitOfMeasure,
    vatNote: {
        css: css`
            ${priceVatLabelStyles()}
        `,
    },
    vatPrice: {
        css: css`
            ${priceStyles()}
            ${priceSecondarySpacingStyles()}
            display: block;
        `,
    },
    changeTooltipAlert: orderV2Props.changeTooltip,
};

const ProductLinePrice: FC<ProductLinePriceProps> = ({
    productLine,
    previousValue,
    extendedStyles,
    overridedStyles,
    labelOverride,
    valueOverride,
    ...productPriceProps
}) => {
    const styles = useExpandProps(productLinePriceStyles, extendedStyles, overridedStyles);

    const {
        productSettings: { showSavingsAmount, showSavingsPercent, enableVat, vatPriceDisplay },
    } = useSettingsCollection();

    if (!productLine) {
        return null;
    }

    if (valueOverride !== undefined) {
        return (
            <SmallHeadingAndText
                extendedStyles={styles.smallHeadingAndText}
                heading={labelOverride ?? translate("Price")}
                text={valueOverride}
                useAriaLabel={true}
            />
        );
    }

    if (productLine.pricing === undefined) {
        const { unitPriceDisplay, unitPriceWithVatDisplay, unitNetPriceDisplay, taxRate, unitOfMeasureDisplay } =
            productLine;
        const showVatPrice = enableVat && vatPriceDisplay !== "DisplayWithoutVat";
        const price = showVatPrice ? unitPriceWithVatDisplay : unitPriceDisplay;

        return (
            <SmallHeadingAndText
                extendedStyles={styles.smallHeadingAndText}
                heading={labelOverride ?? translate("Price")}
                text={
                    <>
                        {price}
                        {productPriceProps.showUnitOfMeasure && unitOfMeasureDisplay ? (
                            <Typography {...styles.priceUnitOfMeasure}>&nbsp;/&nbsp;{unitOfMeasureDisplay}</Typography>
                        ) : null}
                        {enableVat && (
                            <>
                                <Typography {...styles.vatNote}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.vatPrice}>{unitPriceDisplay}</Typography>
                                        <Typography {...styles.vatNote}>{translate("Ex. VAT")}</Typography>
                                    </>
                                )}
                            </>
                        )}
                        {previousValue && previousValue !== unitNetPriceDisplay && (
                            <Tooltip
                                {...styles.changeTooltipAlert}
                                text={translate(
                                    `Price of this item has changed from {0} to {1}`,
                                    previousValue,
                                    unitNetPriceDisplay ?? "",
                                )}
                            />
                        )}
                    </>
                }
                useAriaLabel={true}
            />
        );
    }

    return (
        <ProductPrice
            product={productLine}
            showSavingsAmount={showSavingsAmount}
            showSavingsPercent={showSavingsPercent}
            extendedStyles={styles.productPrice}
            showLabel={true}
            label={labelOverride ?? translate("Price")}
            showUnitOfMeasure={true}
            zeroPriceContactUsLayout="block"
            {...productPriceProps}
        />
    );
};

export default ProductLinePrice;
